import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, FileText } from 'lucide-react';
import { useState } from 'react';
import { pageApi } from '../services/page.service';
import { useWorkspaceStore } from '../stores/workspace.store';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { IconRenderer } from '../lib/icon-map';
import { useToast } from '../hooks/use-toast';
import CreatePageModal from '../components/CreatePageModal';

export default function HomePage() {
  const { currentWorkspace } = useWorkspaceStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: pages, isLoading } = useQuery({
    queryKey: ['pages', currentWorkspace?.id],
    queryFn: () => pageApi.getAll(currentWorkspace!.id),
    enabled: !!currentWorkspace,
  });

  const handleGenerateWithAI = async (prompt: string) => {
    if (!currentWorkspace?.id) return;

    try {
      const result = await pageApi.generateWithAI(currentWorkspace.id, prompt);
      
      toast({
        title: "Páginas geradas com sucesso! ✨",
        description: result.explanation,
      });

      // Recarregar as páginas
      queryClient.invalidateQueries({
        queryKey: ["pages", currentWorkspace.id],
      });
    } catch (error: any) {
      console.error("Erro ao gerar páginas:", error);
      
      let errorMessage = "Ocorreu um erro ao gerar as páginas com IA";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao gerar páginas",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="text-muted-foreground">Carregando páginas...</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">
          Bem-vindo ao {currentWorkspace?.name}
        </h1>
        <p className="text-muted-foreground mt-1">
          Gerencie suas páginas e dashboards customizados
        </p>
      </div>

      {pages && pages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
          {pages.map((page: any) => (
            <Link key={page.id} to={`/pages/${page.slug}`}>
              <Card className="p-6 hover:border-primary/50 hover:shadow-md transition-all h-full">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <IconRenderer icon={page.icon || '📄'} className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">
                    {page.name}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground min-h-[20px]">
                  {page.description || ''}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">
            Nenhuma página criada ainda
          </h3>
          <p className="text-muted-foreground mb-4">
            Crie sua primeira página customizada
          </p>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-5 h-5 mr-2" />
            Criar Primeira Página
          </Button>
        </Card>
      )}

      <CreatePageModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onGenerateWithAI={handleGenerateWithAI}
      />
    </div>
  );
}
